import {
  CognitiveMode,
  ProductSolution,
  SoftSkillCategory,
} from 'generated/graphql';
import { lowerCase } from 'lodash';
import { TSkillOverview } from 'views/Project/components/ProjectJobRolesOverviewSkillsList/ProjectJobRolesOverviewSkillsList.types';

export type TSkillsByCategory = {
  rightForFuture: TSkillOverview[];
  rightForUsSkills: TSkillOverview[];
  rightForRoleSkills: TSkillOverview[];
  cognitiveSummarySkills: TSkillOverview[];
};

export type TMissingCategorySkillsInRoles = {
  name: string;
  missing: string[];
}[];

const COGNITIVE_SKILL_KEYWORDS = ['cognitive'];

export const getCognitiveSoftSkillVersion = (
  name: string,
  category?: SoftSkillCategory
) => {
  if (category === SoftSkillCategory.CognitiveSummary) {
    return 'v2';
  }

  if (
    category === SoftSkillCategory.RightForTheRole &&
    COGNITIVE_SKILL_KEYWORDS.some(keyword =>
      name.toLowerCase().includes(keyword)
    )
  ) {
    return 'v1';
  }

  return null;
};

export const isCategorySkill = (skill: TSkillOverview) => {
  return [
    lowerCase(SoftSkillCategory.RightForTheFuture),
    lowerCase(SoftSkillCategory.RightForTheRole),
    lowerCase(SoftSkillCategory.RightForUs),
    lowerCase(SoftSkillCategory.CognitiveSummary),
  ].includes(skill.name.toLowerCase());
};

export const productSolutionToCognitiveModeMap = {
  [CognitiveMode.InDepth]: [
    ProductSolution.ProfessionalHiring,
    ProductSolution.Reskilling,
    ProductSolution.Learning,
    ProductSolution.SuccessionPlanning,
  ],
  [CognitiveMode.Streamlined]: [
    ProductSolution.EarlyCareerHiring,
    ProductSolution.VolumeHiring,
  ],
};

const CATEGORY_LABELS = {
  RIGHT_FOR_ROLE: "'Right for the role'",
  RIGHT_FOR_US: "'Right for us'",
  RIGHT_FOR_FUTURE: "'Right for the future'",
  COGNITIVE_SUMMARY: "'Cognitive summary'",
} as const;

export const sortSoftSkillsByCategory = (softSkills: TSkillOverview[]) => {
  const categories = {
    rightForFuture: [] as TSkillOverview[],
    rightForUsSkills: [] as TSkillOverview[],
    rightForRoleSkills: [] as TSkillOverview[],
    cognitiveSummarySkills: [] as TSkillOverview[],
  };

  for (const skill of softSkills) {
    switch (skill?.category) {
      case SoftSkillCategory.RightForTheFuture:
        categories.rightForFuture.push(skill);
        break;
      case SoftSkillCategory.RightForUs:
        categories.rightForUsSkills.push(skill);
        break;
      case SoftSkillCategory.RightForTheRole:
        categories.rightForRoleSkills.push(skill);
        break;
      case SoftSkillCategory.CognitiveSummary:
        categories.cognitiveSummarySkills.push(skill);
        break;
    }
  }

  return categories;
};

export const getMissingCategorySkills = (
  softSkills: TSkillOverview[],
  roleName: string
) => {
  const categories = sortSoftSkillsByCategory(softSkills);

  const categoryChecks = [
    {
      skills: categories.rightForRoleSkills,
      label: CATEGORY_LABELS.RIGHT_FOR_ROLE,
    },
    {
      skills: categories.rightForUsSkills,
      label: CATEGORY_LABELS.RIGHT_FOR_US,
    },
    {
      skills: categories.rightForFuture,
      label: CATEGORY_LABELS.RIGHT_FOR_FUTURE,
    },
    {
      skills: categories.cognitiveSummarySkills,
      label: CATEGORY_LABELS.COGNITIVE_SUMMARY,
    },
  ];

  const arrayOfLabels = categoryChecks
    .filter(
      ({ skills }) =>
        skills.length > 0 && !skills.find(skill => isCategorySkill(skill))
    )
    .map(({ label }) => label);

  return arrayOfLabels.length > 0
    ? { name: roleName, missing: arrayOfLabels }
    : null;
};
